import { type PDPdownloadPdfFile, type PublicationDownloadFile } from "../../models/ProductData";
import {
  type PdpPageProductDataDto,
  type TechnicalDetailsData,
  type TechnicalDetailsDataEntry,
} from "../dto/PdpPageProductDataDto";
import { getUnitTagFromString, type PdpPageProductData } from "../models/PdpPageProductData";
import { transformProduct } from "./ProductDataTransformer";
import type { TechnicalInfoDataDTO, TechnicalInfoItemDTO } from "~/Search/dto/SearchResultsDTO";

export interface TechnicalInfoItemDTOExt extends TechnicalInfoItemDTO {
  pageUrl?: string;
  downloadUrl?: string;
}

export function transformPdpProduct(
  source: PdpPageProductDataDto,
  technicalInfo: TechnicalInfoDataDTO | null,
): PdpPageProductData {
  const base = transformProduct(source, true);
  techDataSpecialFormatter(base.technicalDetails);

  return {
    ...base,
    isFullProduct: true,
    description: source.description || "",
    specificationBand: source.categories || [],
    techImages: source.techImages || [],
    preferredProductsFamilyName: source.baseTypeNorm || "",
    tracePartsOrderNo: source.tracePartsOrderNo || "",
    tracePartsUrl: source.tracePartsUrl || "",
    bearingXDesignation: source.bearingXDesignation,
    mountingManagerURL: source.mountingManagerURL || "",
    greaseAppUrl: source.greaseAppUrl || "",
    // hasCad: !!source.tracePartsUrl,
    // hasCalc: !!source.bearingXUrl,
    footnotes: source.footnotes || [],
    attributeIcons: source.attributeIcons || [],
    commentsFootnotes: source.commentsFootnotes,
    downloads: source.downloads ?? [],
    mountingPublicationsDownloads: source.mountingPublicationsDownloads ?? [],
    publications: transformPublications(source.downloads ?? [], source.mountingPublicationsDownloads ?? []),
    showBasicFrequencies: source.showBasicFrequencies,
    showRohsDownload: source.rohsCodes ? true : false,
    unitTag: getUnitTagFromString(source.unitTag?.code),
    technicalInfo: transformTechnicalInfo(technicalInfo) ?? undefined,
    configuratorUrl: source.configuratorUrl,
    reconditionable: source.reconditionable ?? undefined,
    additionalCalculationUrl: source.additionalCalculationUrl ?? undefined,
    lmbtConfigurator: source.lmbtConfigurator,
  };
}

const techDataSpecialFormatter = (technicalDetails: TechnicalDetailsData[] | null): void => {
  // if undefined or null, just skip it
  if (undefined === technicalDetails || null === technicalDetails || technicalDetails.length === 0) {
    return;
  }
  technicalDetails.forEach((list) => {
    if (list.entries.length === 0) {
      return;
    }
    list.entries.forEach((entry) => techDataEntrySpecificWeightUpdate(entry));
  });
};

const techDataEntrySpecificWeightUpdate = (entry: TechnicalDetailsDataEntry): void => {
  if (undefined === entry || null === entry || "text.product.weight" !== entry.description) {
    return;
  }
  entry.abbreviation = "≈m";
};

const transformPublications = (
  downloads: PDPdownloadPdfFile[],
  mounting: PDPdownloadPdfFile[],
): PublicationDownloadFile[] => {
  const publications: PublicationDownloadFile[] = [
    ...transformOldPublicationNewPublication(downloads),
    ...transformOldPublicationNewPublication(mounting),
  ];
  publications.sort((a, b) => a.titleRaw.localeCompare(b.titleRaw));
  return publications;
};

export const transformOldPublicationNewPublication = (old: PDPdownloadPdfFile[]): PublicationDownloadFile[] => {
  // check old for proper data
  if (!old || old.length === 0) {
    return [];
  }
  // set the new list
  let newPublications: PublicationDownloadFile[] = [];
  // push values from OLD publications to NEW publications
  old.forEach((publication) => (newPublications = newPublications.concat(publication.values)));
  // return the fresh list
  return newPublications.map(validatePublicationData);
};

const validatePublicationData = (publication: PublicationDownloadFile): PublicationDownloadFile => {
  return {
    url: publication.url || "", //string;
    title: publication.title || "", //string;
    pubid: publication.pubid || "", //string;
    description: publication.description || "", //string;
    href: publication.href || "", //string;
    size: publication.size || "", //string;
    editor: publication.editor || "", //string;
    filetype: publication.filetype || "", //string;
    format: publication.format || "", //string;
    isdownloadable: publication.isdownloadable || false, //boolean;
    isorderable: publication.isorderable || false, //boolean;
    iswebshop: publication.iswebshop || false, //boolean;
    language: publication.language || "", //string;
    languageCode: publication.languageCode || "", //string;
    languageKey: publication.languageKey || "", //string;
    mediaCategoryCode: publication.mediaCategoryCode || "", //string;
    mediaCategory: publication.mediaCategory || "", //string;
    mediaType: publication.mediaType || "", //string;
    orderNumber: publication.orderNumber || "", //string;
    orderNumberSet: publication.orderNumberSet || false, //boolean;
    pages: publication.pages || "", //string;
    image: publication.image || "", //string;
    shortId: publication.shortId || "", //string;
    shortIdSet: publication.shortIdSet || false, //boolean;
    shortTitle: publication.shortTitle || "", //string;
    shortTitleRaw: publication.shortTitleRaw || "", //string;
    shortTitleVisible: publication.shortTitleVisible || false, //boolean;
    subline: publication.subline || "", //string;
    titleRaw: publication.titleRaw || "", //string;
    titleShow: publication.titleShow || false, //boolean;
    year: publication.year || "", //string;
  };
};

export const transformTechnicalInfo = (technicalInfo: TechnicalInfoDataDTO | null): TechnicalInfoDataDTO | null => {
  if (!technicalInfo?.columns) {
    return technicalInfo;
  }

  return {
    ...technicalInfo,
    columns: technicalInfo.columns.map((column) => ({
      ...column,
      items: column.items.map(({ downloadUrl, pageUrl, ...rest }: TechnicalInfoItemDTOExt) => ({
        ...rest,
        url: downloadUrl ?? pageUrl ?? "",
      })),
    })),
  };
};
